import React, { useState, useCallback, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { FILE_SIZE_LIMITS } from '@/constants/fileSize';
import { fileToBase64 } from '@/utils/fileToBase64';
import type { ImagePromptInput, ControlNetType } from '@/types';

const ACCEPTED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

interface MultiImageUploaderProps {
  images: ImagePromptInput[];
  onChange: (images: ImagePromptInput[]) => void;
  maxImages?: number;
  showControlType?: boolean;
  defaultControlType?: ControlNetType;
  disabled?: boolean;
}

export const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({
  images,
  onChange,
  maxImages = 4,
  showControlType = false,
  defaultControlType = 'ImagePrompt',
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!ACCEPTED_IMAGE_FORMATS.includes(file.type)) {
        return t('image.imageUploader.invalidFormat', {
          defaultValue: 'Invalid file format. Please upload PNG, JPG, JPEG, WEBP, or GIF.',
        });
      }
      if (file.size > FILE_SIZE_LIMITS.IMAGE_MAX_BYTES) {
        return t('image.imageUploader.fileTooLarge', {
          defaultValue: 'File size exceeds {{maxSize}}MB limit.',
          maxSize: FILE_SIZE_LIMITS.IMAGE_MAX_MB,
        });
      }
      return null;
    },
    [t]
  );

  // Add one or more files directly as new reference images (no empty placeholder
  // slot). Dropping or selecting a file goes straight to a ready-to-use image.
  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      setError(null);
      const remaining = maxImages - images.length;
      if (remaining <= 0 || disabled) return;

      const files = Array.from(fileList).slice(0, remaining);
      const accepted: { image: ImagePromptInput; url: string }[] = [];

      for (const file of files) {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          continue;
        }
        try {
          const base64 = await fileToBase64(file);
          accepted.push({
            image: { image: base64, weight: 1.0, stop: 1.0, control_type: defaultControlType },
            url: URL.createObjectURL(file),
          });
        } catch {
          setError(
            t('image.imageUploader.readError', {
              defaultValue: 'Failed to read image file',
            })
          );
        }
      }

      if (accepted.length === 0) return;

      const startIndex = images.length;
      onChange([...images, ...accepted.map((a) => a.image)]);
      setPreviewUrls((prev) => {
        const next = { ...prev };
        accepted.forEach((a, i) => {
          next[startIndex + i] = a.url;
        });
        return next;
      });
    },
    [images, maxImages, disabled, defaultControlType, onChange, validateFile, t]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        void addFiles(files);
      }
    },
    [disabled, addFiles]
  );

  const handleTileClick = useCallback(() => {
    if (!disabled) fileInputRef.current?.click();
  }, [disabled]);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        void addFiles(e.target.files);
      }
      e.target.value = '';
    },
    [addFiles]
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);

      // Clean up preview URL
      const newPreviewUrls = { ...previewUrls };
      delete newPreviewUrls[index];
      // Reindex remaining URLs
      const reindexed: Record<number, string> = {};
      Object.entries(newPreviewUrls).forEach(([key, value]) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reindexed[oldIndex - 1] = value;
        } else {
          reindexed[oldIndex] = value;
        }
      });
      setPreviewUrls(reindexed);
    },
    [images, onChange, previewUrls]
  );

  const handleWeightChange = useCallback(
    (index: number, weight: number) => {
      const newImages = [...images];
      newImages[index] = {
        ...newImages[index],
        weight,
      };
      onChange(newImages);
    },
    [images, onChange]
  );

  const handleStopChange = useCallback(
    (index: number, stop: number) => {
      const newImages = [...images];
      newImages[index] = {
        ...newImages[index],
        stop,
      };
      onChange(newImages);
    },
    [images, onChange]
  );

  const handleControlTypeChange = useCallback(
    (index: number, controlType: ControlNetType) => {
      const newImages = [...images];
      newImages[index] = {
        ...newImages[index],
        control_type: controlType,
      };
      onChange(newImages);
    },
    [images, onChange]
  );

  return (
    <div className="space-y-4">
      {/* Image count */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-secondary-300">
          {t('image.multiImageUploader.imageCount', {
            defaultValue: '{{current}} of {{max}} images',
            current: images.length,
            max: maxImages,
          })}
        </h4>
      </div>

      {/* Image grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Existing images */}
        {images.map((img, imgIndex) => (
          <div
            key={`image-slot-${imgIndex}`}
            className="space-y-3 p-4 border border-gray-200 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800"
          >
            {/* Image preview */}
            <div className="relative">
              <img
                src={previewUrls[imgIndex]}
                alt={`Reference ${imgIndex + 1}`}
                className="w-full h-48 object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemoveImage(imgIndex)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                disabled={disabled}
                aria-label={t('image.multiImageUploader.remove', { defaultValue: 'Remove' })}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Controls */}
            {img.image && (
              <div className="space-y-3">
                {/* Weight slider */}
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-secondary-300 flex items-center justify-between">
                    <span>
                      {t('image.imagePrompt.weight', { defaultValue: 'Weight' })}:{' '}
                      {img.weight.toFixed(2)}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={img.weight}
                    onChange={(e) => handleWeightChange(imgIndex, parseFloat(e.target.value))}
                    disabled={disabled}
                    className="w-full h-2 bg-gray-200 dark:bg-secondary-600 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                    {t('image.imagePrompt.weightHint', {
                      defaultValue: 'How much this image influences the result (0-2)',
                    })}
                  </p>
                </div>

                {/* Stop slider */}
                <div>
                  <label className="text-xs font-medium text-gray-700 dark:text-secondary-300 flex items-center justify-between">
                    <span>
                      {t('image.imagePrompt.stop', { defaultValue: 'Stop' })}: {img.stop.toFixed(2)}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={img.stop}
                    onChange={(e) => handleStopChange(imgIndex, parseFloat(e.target.value))}
                    disabled={disabled}
                    className="w-full h-2 bg-gray-200 dark:bg-secondary-600 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                    {t('image.imagePrompt.stopHint', {
                      defaultValue: "When to stop applying this image's influence (0-1)",
                    })}
                  </p>
                </div>

                {/* Control type selector (optional) */}
                {showControlType && (
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-secondary-300">
                      {t('image.imagePrompt.controlType', { defaultValue: 'Control Type' })}
                    </label>
                    <select
                      value={img.control_type}
                      onChange={(e) =>
                        handleControlTypeChange(imgIndex, e.target.value as ControlNetType)
                      }
                      disabled={disabled}
                      className="select w-full mt-1 text-sm"
                    >
                      <option value="ImagePrompt">
                        {t('image.imagePrompt.controlTypes.ImagePrompt', {
                          defaultValue: 'Image Prompt',
                        })}
                      </option>
                      <option value="FaceSwap">
                        {t('image.imagePrompt.controlTypes.FaceSwap', {
                          defaultValue: 'Face Swap',
                        })}
                      </option>
                      <option value="PyraCanny">
                        {t('image.imagePrompt.controlTypes.PyraCanny', {
                          defaultValue: 'Edge Detection',
                        })}
                      </option>
                      <option value="CPDS">
                        {t('image.imagePrompt.controlTypes.CPDS', {
                          defaultValue: 'Depth Control',
                        })}
                      </option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add image dropzone — drag & drop or click to select (no empty slot) */}
        {images.length < maxImages && (
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={handleTileClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTileClick();
              }
            }}
            onDragEnter={handleDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`h-full min-h-[120px] xs:min-h-[150px] sm:min-h-[200px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-all touch-target ${
              disabled
                ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-secondary-600'
                : isDragging
                  ? 'border-blue-500 bg-blue-100 dark:border-blue-500 dark:bg-blue-900/30 cursor-pointer'
                  : 'border-gray-300 dark:border-secondary-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 cursor-pointer'
            }`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                isDragging ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-secondary-700'
              }`}
            >
              <Plus
                className={`w-6 h-6 ${isDragging ? 'text-blue-600' : 'text-gray-400 dark:text-secondary-400'}`}
              />
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-secondary-300">
              {t('image.imagePrompt.addImage', { defaultValue: 'Add Image' })}
            </span>
            <span className="text-xs text-gray-500 dark:text-secondary-400 mt-1 text-center px-2">
              {t('image.multiImageUploader.dragOrClick', {
                defaultValue: 'Drag & drop or click to add',
              })}
            </span>
          </div>
        )}
      </div>

      {/* Hidden input shared by the dropzone (sibling, not nested) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_IMAGE_FORMATS.join(',')}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
      />

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
};
